// =========================
// RecommendationPanel
// Reads `recommendation` + `forecast` from the analyze-property response.
// Displays the decision badge (BUY / CONSIDER / HOLD / PASS),
// signals, and reasoning, plus confidence + volatility chips.
// =========================

import React from "react";
import { NOT_ADVICE_TEXT } from "./NotAdviceDisclaimer";

const DECISION_STYLE = {
  BUY:                { bg: "#dcfce7", fg: "#166534", border: "#86efac", label: "BUY" },
  "CAUTIOUS BUY":     { bg: "#d1fae5", fg: "#065f46", border: "#6ee7b7", label: "CAUTIOUS BUY" },
  "STRONG CONSIDER":  { bg: "#fef3c7", fg: "#92400e", border: "#fcd34d", label: "STRONG CONSIDER" },
  CONSIDER:           { bg: "#fef9c3", fg: "#854d0e", border: "#fde68a", label: "CONSIDER" },
  HOLD:               { bg: "#fee2e2", fg: "#991b1b", border: "#fecaca", label: "HOLD" },
  "IMPROVE DEAL":     { bg: "#ffedd5", fg: "#9a3412", border: "#fdba74", label: "IMPROVE DEAL" },
  PASS:               { bg: "#fce7f3", fg: "#9d174d", border: "#fbcfe8", label: "PASS" },
  // v1.0.0 Phase 3 — lease-leverage overlay labels. Same indigo family as
  // the entry-type chip on /leads cards so the relationship is visible.
  // LEASE — CONSIDER uses a lighter indigo to signal "positive but not as
  // strong" relative to LEASE, parallel to STRONG CONSIDER vs BUY.
  LEASE:              { bg: "#e0e7ff", fg: "#3730a3", border: "#a5b4fc", label: "LEASE" },
  "LEASE — CONSIDER": { bg: "#eef2ff", fg: "#4338ca", border: "#c7d2fe", label: "LEASE — CONSIDER" },
  DEFAULT:            { bg: "#e5e7eb", fg: "#374151", border: "#d1d5db", label: "N/A" },
};

function styleFor(decision) {
  if (!decision) return DECISION_STYLE.DEFAULT;
  return DECISION_STYLE[decision] || { ...DECISION_STYLE.DEFAULT, label: decision };
}

function fmtPct(n) {
  if (typeof n !== "number" || !isFinite(n)) return "—";
  if (n <= 1.0001 && n >= 0) return `${Math.round(n * 100)}%`;
  return `${Math.round(n)}%`;
}

function fmtNum(n, digits = 2) {
  if (typeof n !== "number" || !isFinite(n)) return "—";
  return n.toFixed(digits);
}

const Chip = ({ label, value, tone = "default" }) => {
  const tones = {
    default: { bg: "#f3f4f6", fg: "#374151" },
    good:    { bg: "#dcfce7", fg: "#166534" },
    warn:    { bg: "#fef9c3", fg: "#854d0e" },
    bad:     { bg: "#fee2e2", fg: "#991b1b" },
  };
  const t = tones[tone] || tones.default;
  return (
    <div style={{
      display: "inline-flex", flexDirection: "column", alignItems: "flex-start",
      padding: "8px 12px", borderRadius: 8, background: t.bg, color: t.fg, marginRight: 8, marginTop: 8,
      minWidth: 110,
    }}>
      <div style={{ fontSize: 11, opacity: 0.75, textTransform: "uppercase", letterSpacing: 0.5 }}>{label}</div>
      <div style={{ fontSize: 18, fontWeight: 700 }}>{value}</div>
    </div>
  );
};

export default function RecommendationPanel({ recommendation, forecast }) {
  const decision = recommendation?.decision || "DEFAULT";
  const s = styleFor(decision);

  const rawScore = recommendation?.score;
  const signals = Array.isArray(recommendation?.signals) ? recommendation.signals : [];
  const reasoning = recommendation?.reasoning;

  // The engine emits a SIGNED score roughly in [-100, +100]. Map onto a
  // friendlier 0–100 display where 50 = neutral. Raw value is kept visible
  // in a small caption for transparency / future debugging.
  const normalizedScore =
    typeof rawScore === "number"
      ? Math.round(Math.max(0, Math.min(100, (rawScore + 100) / 2)))
      : null;

  const scoreTone =
    normalizedScore === null ? "#9ca3af"
      : normalizedScore >= 67 ? "#16a34a"
      : normalizedScore >= 34 ? "#f59e0b"
      : "#dc2626";

  const confidence = forecast?.confidenceScore;
  const volatility = forecast?.volatilityIndex;
  const direction = forecast?.direction;

  const confidenceTone = confidence > 0.66 ? "good" : confidence > 0.33 ? "warn" : "bad";
  const volatilityTone = volatility < 0.33 ? "good" : volatility < 0.66 ? "warn" : "bad";

  return (
    <div style={{
      background: "#ffffff",
      border: "1px solid #e5e7eb",
      borderRadius: 12,
      padding: 20,
    }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 12 }}>
        <div>
          <div style={{ fontSize: 12, color: "#6b7280", textTransform: "uppercase", letterSpacing: 1 }}>
            Opportunity Signal
          </div>
          <div style={{
            display: "inline-block",
            marginTop: 6,
            padding: "6px 14px",
            borderRadius: 999,
            background: s.bg,
            color: s.fg,
            border: `1px solid ${s.border}`,
            fontWeight: 800,
            fontSize: 18,
            letterSpacing: 1,
          }}>
            {s.label}
          </div>
          {/* EA-002 / ADR-029 / ADR-044: decision-support intelligence, not advice.
              Canonical wording shared via NotAdviceDisclaimer (PLATFORM-002). */}
          <div style={{ fontSize: 11, color: "#9ca3af", marginTop: 6, maxWidth: 260 }}>
            {NOT_ADVICE_TEXT}
          </div>
        </div>
        <div style={{ textAlign: "right" }}>
          <div style={{ fontSize: 12, color: "#6b7280", textTransform: "uppercase", letterSpacing: 1 }}>
            Opportunity Score
          </div>
          <div style={{ display: "flex", alignItems: "baseline", justifyContent: "flex-end", gap: 6 }}>
            <div style={{ fontSize: 32, fontWeight: 800, color: scoreTone, lineHeight: 1 }}>
              {normalizedScore !== null ? normalizedScore : "—"}
            </div>
            <div style={{ fontSize: 14, color: "#9ca3af", fontWeight: 600 }}>/ 100</div>
          </div>
          <div style={{ fontSize: 11, color: "#9ca3af", marginTop: 4 }}>
            50 = neutral · raw {typeof rawScore === "number" ? fmtNum(rawScore, 1) : "—"}
          </div>
        </div>
      </div>

      <div style={{ marginTop: 4, display: "flex", flexWrap: "wrap" }}>
        <Chip
          label="Direction"
          value={direction ? (direction.charAt(0).toUpperCase() + direction.slice(1)) : "—"}
          tone={
            direction === "upward" || direction === "bullish" ? "good"
              : direction === "downward" || direction === "bearish" ? "bad"
              : "default"
          }
        />
        <Chip label="Confidence" value={fmtPct(confidence)} tone={confidenceTone} />
        <Chip label="Volatility" value={fmtPct(volatility)} tone={volatilityTone} />
      </div>

      {signals.length > 0 && (
        <div style={{ marginTop: 18 }}>
          <div style={{ fontSize: 12, color: "#6b7280", textTransform: "uppercase", letterSpacing: 1, marginBottom: 8 }}>
            Signals
          </div>
          <ul style={{ margin: 0, paddingLeft: 18, color: "#374151", fontSize: 14, lineHeight: 1.6 }}>
            {signals.map((sig, i) => (
              <li key={i}>{typeof sig === "string" ? sig : (sig?.label || sig?.message || JSON.stringify(sig))}</li>
            ))}
          </ul>
        </div>
      )}

      {reasoning && (
        <div style={{ marginTop: 18 }}>
          <div style={{ fontSize: 12, color: "#6b7280", textTransform: "uppercase", letterSpacing: 1, marginBottom: 6 }}>
            Reasoning
          </div>
          <div style={{ fontSize: 14, color: "#374151", lineHeight: 1.6 }}>
            {typeof reasoning === "string"
              ? reasoning
              : Array.isArray(reasoning)
                ? reasoning.join(" · ")
                : JSON.stringify(reasoning)}
          </div>
        </div>
      )}
    </div>
  );
}
